import { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  FormControlLabel,
  Checkbox,
  Link,
  Typography
} from '@mui/material';

interface RegisterDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    first: string;
    last: string;
    email: string;
    pass: string;
    acceptedTerms: boolean;
  }) => void;
  loading: boolean;
  error: string | null;
}

const RegisterDialog: FC<RegisterDialogProps> = ({
  open,
  onClose,
  onSubmit,
  loading,
  error
}) => {
  // Local input field tracks
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [localValidationError, setLocalValidationError] = useState<
    string | null
  >(null);

  // 🧹 Helper engine to clear out data traces completely
  const resetFormFields = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword('');
    setAcceptedTerms(false);
    setLocalValidationError(null);
  };

  // 🚀 AUTOMATIC RESET: Watch the 'open' status prop tracking visibility.
  // The absolute millisecond the dialog closes (whether from success or cancellation), wipe the text states.
  useEffect(() => {
    if (!open) {
      resetFormFields();
    }
  }, [open]);

  const handleFormSubmit = () => {
    setLocalValidationError(null);

    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !password.trim()
    ) {
      setLocalValidationError(
        'All fields are required to establish an account.'
      );
      return;
    }

    if (!acceptedTerms) {
      setLocalValidationError(
        'You must accept the terms and conditions to proceed.'
      );
      return;
    }

    onSubmit({
      first: firstName,
      last: lastName,
      email,
      pass: password,
      acceptedTerms
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem', pb: 1 }}>
        Create Your Account
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          {localValidationError && (
            <Alert severity="warning" sx={{ width: '100%' }}>
              {localValidationError}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}

          <Box display="flex" gap={2}>
            <TextField
              label="First Name"
              fullWidth
              size="small"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={loading}
            />
            <TextField
              label="Last Name"
              fullWidth
              size="small"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={loading}
            />
          </Box>

          <TextField
            label="Email Address"
            type="email"
            fullWidth
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />

          <TextField
            label="Password"
            type="password"
            fullWidth
            size="small"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                color="primary"
                disabled={loading}
              />
            }
            label={
              <Box
                variant="body2"
                component={Typography}
                sx={{ fontSize: '0.85rem' }}
              >
                I agree to the{' '}
                <Link href="/terms" underline="hover">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link href="/privacy" underline="hover">
                  Privacy Policy
                </Link>
                .
              </Box>
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#fcfdfd' }}>
        {/* Trigger standard onClose, which trips the useEffect hook cleanup above */}
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleFormSubmit}
          variant="contained"
          disabled={loading}
          sx={{ textTransform: 'none', px: 3 }}
        >
          {loading ? 'Registering...' : 'Register'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegisterDialog;
